import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  assignReviewThread,
  assignReviewThreadToSelf,
  addReviewThreadCommentBySubjectId,
  getAuthorshipById,
  getCourseCertificateById,
  getDispensationById,
  getInsurancePolicyById,
  getReductionById,
  getLegalAidById,
  getAuditTrailBySubjectId,
  getReviewThreadCommentsBySubjectId,
  getTeachingById,
  markReviewThreadResolved,
  reclassifyReviewThreadBySubjectId,
  type AddDocumentCommentRequest,
  type DocumentAuditTrailModel,
  type DocumentCommentModel,
  type DocumentReclassifyStatus,
  type ReclassifyDocumentResponse,
  type ReviewThreadListItemModel,
} from "@/api/cle-api";

import { authorshipQueryKey } from "./useAuthorships";
import { courseCertificateQueryKey } from "./useCourseCertificates";
import { dispensationQueryKey } from "./useDispensations";
import { insurancePolicyQueryKey } from "./useInsurancePolicies";
import { reductionQueryKey } from "./useReductions";
import { legalAidQueryKey } from "./useLegalAid";
import { teachingQueryKey } from "./useTeachings";

export const reviewThreadAuditTrailQueryKey = (subjectId?: string) =>
  ["cle-api", "admin", "review-threads", subjectId, "audit-trail"] as const;

export const reviewThreadCommentsQueryKey = (subjectId?: string) =>
  ["cle-api", "admin", "review-threads", subjectId, "comments"] as const;

export interface AddReviewThreadCommentParams extends AddDocumentCommentRequest {
  subjectId: string;
  userId?: string;
}

export interface ReclassifyReviewThreadParams {
  subjectId: string;
  userId: string;
  newStatus: DocumentReclassifyStatus;
}

type EvidenceWithReviewThread = {
  id?: string;
  documentId?: string | null;
  ownerUserId?: string;
  reviewThread?: ReviewThreadListItemModel | null;
};

export const mergeEvidenceDetailUpdate = <T extends EvidenceWithReviewThread>(
  previous: T | undefined,
  updated: T,
): T => ({
  ...(previous ?? updated),
  ...updated,
  reviewThread: updated.reviewThread ?? previous?.reviewThread ?? null,
});

export const mergeEvidenceDetailWithOverride = <
  T extends EvidenceWithReviewThread,
>(
  data: T | undefined,
  override: T | undefined,
): T | undefined => {
  if (!data) {
    return override;
  }

  if (!override) {
    return data;
  }

  return {
    ...override,
    reviewThread: data.reviewThread ?? override.reviewThread ?? null,
  };
};

const EVIDENCE_DETAIL_RESOURCES = new Set([
  "reductions",
  "legal-aid",
  "course-certificates",
  "teachings",
  "authorships",
  "insurance-policies",
  "dispensations",
]);

const isCachedEvidenceDetailQuery = (queryKey: readonly unknown[]): boolean =>
  queryKey.length === 5 &&
  queryKey[0] === "cle-api" &&
  queryKey[1] === "users" &&
  typeof queryKey[2] === "string" &&
  typeof queryKey[3] === "string" &&
  typeof queryKey[4] === "string" &&
  EVIDENCE_DETAIL_RESOURCES.has(queryKey[3]);

const matchesReviewThreadEvidence = (
  data: EvidenceWithReviewThread,
  thread: ReviewThreadListItemModel,
): boolean => {
  if (data.reviewThread?.id === thread.id) {
    return true;
  }

  const subjectIds = new Set(
    [thread.subjectId, thread.documentId].filter(
      (value): value is string => !!value,
    ),
  );

  return (
    (data.id != null && subjectIds.has(data.id)) ||
    (data.documentId != null && subjectIds.has(data.documentId))
  );
};

export const patchEvidenceReviewThreadInCache = (
  queryClient: QueryClient,
  thread: ReviewThreadListItemModel,
) => {
  const queries = queryClient.getQueriesData<EvidenceWithReviewThread>({
    predicate: (query) => isCachedEvidenceDetailQuery(query.queryKey),
  });

  for (const [queryKey, data] of queries) {
    if (!data || !matchesReviewThreadEvidence(data, thread)) {
      continue;
    }

    queryClient.setQueryData(queryKey, { ...data, reviewThread: thread });
  }
};

const matchesEvidenceSubject = (
  data: EvidenceWithReviewThread,
  subjectId: string,
): boolean => data.id === subjectId || data.documentId === subjectId;

const refetchAndPatchEvidenceDetailInCache = async (
  queryClient: QueryClient,
  subjectId: string,
  userId: string,
): Promise<void> => {
  const entries: Array<{
    queryKey: readonly unknown[];
    fetchDetail: () => Promise<EvidenceWithReviewThread>;
  }> = [
    {
      queryKey: reductionQueryKey({ userId, reductionId: subjectId }),
      fetchDetail: () => getReductionById({ userId, reductionId: subjectId }),
    },
    {
      queryKey: legalAidQueryKey({ userId, legalAidId: subjectId }),
      fetchDetail: () => getLegalAidById({ userId, legalAidId: subjectId }),
    },
    {
      queryKey: courseCertificateQueryKey({ userId, id: subjectId }),
      fetchDetail: () => getCourseCertificateById({ userId, id: subjectId }),
    },
    {
      queryKey: teachingQueryKey({ userId, teachingId: subjectId }),
      fetchDetail: () => getTeachingById({ userId, teachingId: subjectId }),
    },
    {
      queryKey: authorshipQueryKey({ userId, authorshipId: subjectId }),
      fetchDetail: () => getAuthorshipById({ userId, authorshipId: subjectId }),
    },
    {
      queryKey: insurancePolicyQueryKey({
        userId,
        insurancePolicyId: subjectId,
      }),
      fetchDetail: () =>
        getInsurancePolicyById({ userId, insurancePolicyId: subjectId }),
    },
    {
      queryKey: dispensationQueryKey({ userId, dispensationId: subjectId }),
      fetchDetail: () =>
        getDispensationById({ userId, dispensationId: subjectId }),
    },
  ];

  for (const { queryKey, fetchDetail } of entries) {
    const cached = queryClient.getQueryData<EvidenceWithReviewThread>(queryKey);
    if (!cached || !matchesEvidenceSubject(cached, subjectId)) {
      continue;
    }

    const data = await fetchDetail();
    queryClient.setQueryData(
      queryKey,
      (old: EvidenceWithReviewThread | undefined) =>
        mergeEvidenceDetailUpdate(old, data),
    );

    return;
  }
};

const resolveEvidenceOwnerUserId = (
  queryClient: QueryClient,
  subjectId: string,
): string | undefined => {
  const queries = queryClient.getQueriesData<EvidenceWithReviewThread>({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        key[0] === "cle-api" &&
        key[1] === "users" &&
        typeof key[2] === "string" &&
        key[3] !== "pdf"
      );
    },
  });

  for (const [, data] of queries) {
    if (data && matchesEvidenceSubject(data, subjectId) && data.ownerUserId) {
      return data.ownerUserId;
    }
  }

  return undefined;
};

export const invalidateEvidenceDetailQueries = (
  queryClient: QueryClient,
  subjectId: string,
  userId?: string,
) => {
  if (!userId) {
    return;
  }

  void queryClient.invalidateQueries({
    queryKey: courseCertificateQueryKey({ userId, id: subjectId }),
  });
  void queryClient.invalidateQueries({
    queryKey: reductionQueryKey({ userId, reductionId: subjectId }),
  });
  void queryClient.invalidateQueries({
    queryKey: legalAidQueryKey({ userId, legalAidId: subjectId }),
  });
  void queryClient.invalidateQueries({
    queryKey: teachingQueryKey({ userId, teachingId: subjectId }),
  });
  void queryClient.invalidateQueries({
    queryKey: authorshipQueryKey({ userId, authorshipId: subjectId }),
  });
  void queryClient.invalidateQueries({
    queryKey: insurancePolicyQueryKey({
      userId,
      insurancePolicyId: subjectId,
    }),
  });
  void queryClient.invalidateQueries({
    queryKey: dispensationQueryKey({ userId, dispensationId: subjectId }),
  });
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "users", userId, "course-certificates"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "users", userId, "reductions"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "users", userId, "legal-aid"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "users", userId, "teachings"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "users", userId, "authorships"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "users", userId, "insurance-policies"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "users", userId, "dispensations"],
  });
};

const invalidateAdminReviewThreadsList = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({
    queryKey: ["cle-api", "admin", "review-threads"],
  });
};

const invalidateReviewThreadQueries = (
  queryClient: QueryClient,
  subjectId: string,
  userId?: string,
) => {
  invalidateEvidenceDetailQueries(queryClient, subjectId, userId);
  invalidateReviewThreadSidecarQueries(queryClient, subjectId);
};

const invalidateReviewThreadSidecarQueries = (
  queryClient: QueryClient,
  subjectId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: reviewThreadAuditTrailQueryKey(subjectId),
  });
  queryClient.invalidateQueries({
    queryKey: reviewThreadCommentsQueryKey(subjectId),
  });
  invalidateAdminReviewThreadsList(queryClient);
};

export const useReviewThreadAuditTrail = (
  subjectId?: string,
): UseQueryResult<DocumentAuditTrailModel[], Error> =>
  useQuery({
    queryKey: reviewThreadAuditTrailQueryKey(subjectId),
    queryFn: () => getAuditTrailBySubjectId(subjectId!),
    enabled: !!subjectId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

export const useReviewThreadComments = (
  subjectId?: string,
): UseQueryResult<DocumentCommentModel[], Error> =>
  useQuery({
    queryKey: reviewThreadCommentsQueryKey(subjectId),
    queryFn: () => getReviewThreadCommentsBySubjectId(subjectId!),
    enabled: !!subjectId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

export const useAddReviewThreadComment = (): UseMutationResult<
  DocumentCommentModel,
  Error,
  AddReviewThreadCommentParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subjectId, content }) =>
      addReviewThreadCommentBySubjectId(subjectId, { content }),
    onSuccess: async (comment, { subjectId, userId }) => {
      queryClient.setQueryData(
        reviewThreadCommentsQueryKey(subjectId),
        (old: DocumentCommentModel[] | undefined) => [...(old ?? []), comment],
      );

      const ownerUserId =
        userId ?? resolveEvidenceOwnerUserId(queryClient, subjectId);
      if (ownerUserId) {
        await refetchAndPatchEvidenceDetailInCache(
          queryClient,
          subjectId,
          ownerUserId,
        );
      }

      invalidateAdminReviewThreadsList(queryClient);
    },
  });
};

export const useReclassifyReviewThread = (): UseMutationResult<
  ReclassifyDocumentResponse,
  Error,
  ReclassifyReviewThreadParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subjectId, newStatus }) =>
      reclassifyReviewThreadBySubjectId(subjectId, newStatus),
    onSuccess: async (_, { subjectId, userId }) => {
      await refetchAndPatchEvidenceDetailInCache(
        queryClient,
        subjectId,
        userId,
      );
      invalidateReviewThreadQueries(queryClient, subjectId, userId);
    },
  });
};

export interface AssignReviewThreadParams {
  threadId: string;
  /** Den tildelte navngives ved e-mail; CLE-API'et udleder selv admin-id'et. */
  assigneeEmail: string;
  adminDisplayName?: string;
}

export const useAssignReviewThread = (): UseMutationResult<
  ReviewThreadListItemModel,
  Error,
  AssignReviewThreadParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, assigneeEmail, adminDisplayName }) =>
      assignReviewThread(threadId, { assigneeEmail, adminDisplayName }),
    onSuccess: (data, variables) => {
      const thread: ReviewThreadListItemModel = {
        ...data,
        assignedAdminDisplayName:
          data.assignedAdminDisplayName ?? variables.adminDisplayName ?? null,
      };
      patchEvidenceReviewThreadInCache(queryClient, thread);
      invalidateReviewThreadSidecarQueries(queryClient, data.subjectId);
    },
  });
};

export const useAssignReviewThreadToSelf = (): UseMutationResult<
  ReviewThreadListItemModel,
  Error,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignReviewThreadToSelf,
    onSuccess: (data) => {
      patchEvidenceReviewThreadInCache(queryClient, data);
      invalidateReviewThreadSidecarQueries(queryClient, data.subjectId);
    },
  });
};

export const useMarkReviewThreadResolved = (): UseMutationResult<
  ReviewThreadListItemModel,
  Error,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markReviewThreadResolved,
    onSuccess: (data) => {
      patchEvidenceReviewThreadInCache(queryClient, data);
      invalidateReviewThreadSidecarQueries(queryClient, data.subjectId);
    },
  });
};
